// src/audio/capture.ts — microphone capture yielding 16 kHz mono PCM.
// Serves both modes: listen streams frames (onFrame); talk buffers a whole clip
// (startBuffering/stopBuffering). The AudioWorklet posts Float32 frames; we convert to
// Int16 and resample to 16 kHz here (grounding §2.2 / §5.2).
import { LIVE_INPUT_HZ } from "../config";
import { resampleLinear } from "../core/pcm";

export interface AudioCapture {
  onFrame(cb: (pcm16k: Int16Array) => void): void; // streaming (listen)
  startBuffering(): void; // talk: begin accumulating
  stopBuffering(): Int16Array; // talk: return the whole clip @16 kHz
  close(): void;
}

function floatToInt16(frame: Float32Array): Int16Array {
  const out = new Int16Array(frame.length);
  for (let i = 0; i < frame.length; i++) {
    const s = Math.max(-1, Math.min(1, frame[i]));
    out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return out;
}

export async function openCapture(): Promise<AudioCapture> {
  const stream = await navigator.mediaDevices.getUserMedia({
    audio: {
      channelCount: 1,
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
    },
  });
  const ctx = new AudioContext();
  await ctx.audioWorklet.addModule("/capture-processor.js");
  const src = ctx.createMediaStreamSource(stream);
  const node = new AudioWorkletNode(ctx, "capture-processor");
  src.connect(node);
  // node is intentionally not connected to ctx.destination (we don't monitor the mic).

  const rate = ctx.sampleRate; // hardware rate, often 48000
  let frameCb: ((p: Int16Array) => void) | null = null;
  let buffering = false;
  const buf: Float32Array[] = [];

  node.port.onmessage = (e: MessageEvent<Float32Array>) => {
    const frame = e.data;
    if (buffering) buf.push(frame);
    if (frameCb) frameCb(resampleLinear(floatToInt16(frame), rate, LIVE_INPUT_HZ));
  };

  return {
    onFrame(cb) {
      frameCb = cb;
    },
    startBuffering() {
      buffering = true;
      buf.length = 0;
    },
    stopBuffering() {
      buffering = false;
      const total = buf.reduce((n, a) => n + a.length, 0);
      const all = new Float32Array(total);
      let o = 0;
      for (const a of buf) {
        all.set(a, o);
        o += a.length;
      }
      buf.length = 0;
      // Resample the whole clip in one pass (clean, no per-frame boundary artifacts).
      return resampleLinear(floatToInt16(all), rate, LIVE_INPUT_HZ);
    },
    close() {
      try {
        node.port.onmessage = null;
        node.disconnect();
        src.disconnect();
        stream.getTracks().forEach((t) => t.stop());
        void ctx.close();
      } catch {
        /* already closed */
      }
    },
  };
}
